// src/components/QuoteSummaryDetail.js
import React from 'react';

const formatTitle = (key) => {
  // Special cases for acronyms and specific terms
  const specialCases = {
    'vpn': 'VPN',
    'privateNlb': 'Private NLB',
    'publicNlb': 'Public NLB',
    'privateAlb': 'Private ALB',
    'publicAlb': 'Public ALB',
    'natGateway': 'NAT Gateway',
    // 'cognito': 'AWS Cognito',
    // 'cloudfront': 'CloudFront',
    's3Storage': 'S3 Storage',
    'instanceStorage': 'EBS Storage',
    'uatStorage': 'UAT EBS Storage',
    'drStorage': 'DR EBS Storage',
    'production': 'Production',
    'uat': 'UAT',
    'dr': 'DR'
  };

  return specialCases[key] || key;
};

const safeNumber = (value) => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

const CostSection = ({ title, items, showDescription = false, state }) => (
  <div className="space-y-2">
    <h3 className="font-medium text-lg text-gray-900">{title}</h3>
    <div className="grid grid-cols-2 gap-2 text-sm">
      {Object.entries(items || {}).map(([key, item]) => (
        (item && safeNumber(item.cost) > 0.001 && key !== 'natGateway') && (
          <React.Fragment key={key}>
            <div className="text-gray-600">
              <div>{formatTitle(key)}</div>
              {showDescription && item.description && (
                <div className="text-xs text-gray-500 mt-1">{item.description}</div>
              )}
            </div>
            <div className="text-right">
              <div className="text-gray-900">AUD ${safeNumber(item.cost).toFixed(2)}</div>
            </div>
          </React.Fragment>
        )
      ))}
      {title === "Network Costs" && state.requiresInternet && items.natGateway && (
        <React.Fragment key="natGateway">
          <div className="text-gray-600">
            <div>{formatTitle('natGateway')}</div>
            {showDescription && items.natGateway.description && (
              <div className="text-xs text-gray-500 mt-1">{items.natGateway.description}</div>
            )}
          </div>
          <div className="text-right">
            <div className="text-gray-900">AUD ${safeNumber(items.natGateway.cost).toFixed(2)}</div>
          </div>
        </React.Fragment>
      )}
    </div>
  </div>
);

const QuoteSummaryDetail = ({ quote, state }) => {
  if (!quote) return null;

  const { infrastructure, support, summary } = quote;

  return (
    <div className="space-y-6 bg-white rounded-lg shadow p-6">
      {/* AWS Infrastructure */}
      <div className="space-y-6">
        <div className="pb-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">AWS Infrastructure</h2>
          <p className="text-sm text-gray-500 mt-1">Base AWS services and resources (costs passed through directly from AWS)</p> {/* Added clarification here */}
        </div>

        <CostSection
          title="Environment Costs"
          items={infrastructure?.costs?.environment}
          showDescription
          state={state}
        />

        <CostSection
          title="Network Costs"
          items={infrastructure?.costs?.network}
          showDescription
          state={state}
        />

        <CostSection
          title="Storage Costs"
          items={infrastructure?.costs?.storage}
          showDescription
          state={state}
        />

        <div className="pt-2 border-t">
          <div className="flex justify-between items-center text-gray-900">
            <span className="font-medium">Infrastructure Subtotal</span>
            <span className="font-medium">AUD ${safeNumber(infrastructure?.total).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Support & Management */}
      <div className="space-y-4 border-t pt-6">
        <h2 className="text-xl font-semibold text-gray-900">Support & Management</h2>
        <p className="text-sm text-gray-500">24/7 support and infrastructure management</p>

        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="font-medium text-blue-900">Core Support Services</h3>
              <p className="text-sm text-blue-700 mt-1">Based on infrastructure size</p>
            </div>
            <span className="font-medium text-blue-900">AUD ${safeNumber(support.totalBeforeDiscount).toFixed(2)}</span>
          </div>
          <div className="text-sm text-blue-800">
            <p className="mb-2">Includes:</p>
            <ul className="list-disc pl-5 space-y-1">
              {support.costs.included.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contract Discount */}
        <div className="flex justify-between items-center text-sm">
          <span className="text-gray-600">
            Contract Discount ({support.discountPercentage}% off Support & Management Fee)
          </span>
          <span className="text-green-600">-AUD ${safeNumber(support.discountAmount).toFixed(2)}</span>
        </div>

        {/* Discounted Support Total */}
        <div className="flex justify-between items-center pt-2 border-t">
          <span className="font-medium text-gray-900">Support & Management (After Discount)</span>
          <span className="font-medium text-gray-900">AUD ${safeNumber(support.total).toFixed(2)}</span>
        </div>
      </div>

      {/* Total Summary */}
      <div className="space-y-3 border-t pt-6">
        <div className="flex justify-between items-center text-lg">
          <span className="font-medium text-gray-900">Infrastructure Subtotal</span>
          <span className="font-medium text-gray-900">AUD ${safeNumber(infrastructure.total).toFixed(2)}</span>
        </div>
        <div className="flex justify-between items-center text-lg">
          <span className="font-medium text-gray-900">Support & Management Subtotal</span>
          <span className="font-medium text-gray-900">AUD ${safeNumber(support.total).toFixed(2)}</span>
        </div>
        <div className="flex justify-between items-center pt-3 border-t text-xl font-bold">
          <span>Monthly Total</span>
          <span>AUD ${safeNumber(summary.finalTotal).toFixed(2)}</span>
        </div>
      </div>

      <div className="text-sm text-gray-500 space-y-1 border-t pt-4 mt-4">
        <p>* All prices exclude GST</p>
        <p>* AWS Infrastructure costs are indicative and subject to change based on actual usage and AWS pricing.</p> {/* Added disclaimer */}
        <p>* Actual costs may vary based on usage</p>
      </div>
    </div>
  );
};

export default QuoteSummaryDetail;
